// Vsj store: local copy of the server-owned view-state, RFC6902-subset patch
// apply (add/remove/replace), and changed-path subscriptions: components
// subscribe to top-level key prefixes and re-render when a patch touches
// them.

import type { Vsj, PatchOp } from './types';

type Listener = (changed: Set<string>) => void;

let doc: Vsj = {};
const listeners: Listener[] = [];

export function get(): Vsj {
  return doc;
}

/** Dotted-path lookup convenience: at('fight.player.hp'). */
export function at(path: string): any {
  let cur: any = doc;
  for (const part of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[part];
  }
  return cur;
}

export function onChange(fn: Listener): void {
  listeners.push(fn);
}

function emit(changed: Set<string>): void {
  for (const fn of listeners) fn(changed);
}

export function setFull(v: Vsj): void {
  const changed = new Set<string>([...Object.keys(doc), ...Object.keys(v), '*']);
  doc = v;
  emit(changed);
}

function unescapeToken(t: string): string {
  return t.replace(/~1/g, '/').replace(/~0/g, '~');
}

/** Apply a patch list; returns false (caller should resync) on any failure. */
export function applyPatch(ops: PatchOp[]): boolean {
  const changed = new Set<string>();
  for (const op of ops) {
    const tokens = op.path.split('/').slice(1).map(unescapeToken);
    if (!tokens.length) {
      if (op.op === 'replace' && op.value && typeof op.value === 'object') {
        doc = op.value as Vsj;
        changed.add('*');
        continue;
      }
      return false;
    }
    changed.add(tokens[0]);
    let parent: any = doc;
    for (let i = 0; i < tokens.length - 1; i++) {
      parent = parent?.[tokens[i]];
      if (parent == null || typeof parent !== 'object') return false;
    }
    const key = tokens[tokens.length - 1];
    const isArr = Array.isArray(parent);
    switch (op.op) {
      case 'add':
        if (isArr) {
          const idx = key === '-' ? parent.length : Number(key);
          if (Number.isNaN(idx) || idx < 0 || idx > parent.length) return false;
          parent.splice(idx, 0, op.value);
        } else parent[key] = op.value;
        break;
      case 'replace':
        if (isArr) {
          const idx = Number(key);
          if (Number.isNaN(idx) || idx < 0 || idx >= parent.length) return false;
          parent[idx] = op.value;
        } else {
          if (!(key in parent)) return false;
          parent[key] = op.value;
        }
        break;
      case 'remove':
        if (isArr) {
          const idx = Number(key);
          if (Number.isNaN(idx) || idx < 0 || idx >= parent.length) return false;
          parent.splice(idx, 1);
        } else {
          if (!(key in parent)) return false;
          delete parent[key];
        }
        break;
      default:
        return false;
    }
  }
  emit(changed);
  return true;
}

/** infos registry lookup. */
export function info(id: string): any {
  return doc.infos?.[id];
}

/** Display name for an id via infos (never a raw id when avoidable). */
export function infoName(id: string): string {
  return String(info(id)?.name ?? id);
}
